import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  ColumnDef,
  flexRender,
  SortingState
} from "@tanstack/react-table";
import { Image } from "react-bootstrap";
import cn from 'classnames';
import React, { useEffect, useMemo, useState } from "react";
import UserCard from "../../../../../../../component/common/UserCard/UserCard";
import { ASSESTS } from "../../../../../../../constant/assest";
import CustomButton, { ButtonFont, ButtonSize, ButtonType } from "../../../../../../../component/common/CustomButton/CustomButton";
import Pagination from "../../../../../../../component/UI/Pagination/Pagination";
import { AppDispatch, RootState } from "../../../../../../../redux/store";
import { fetchUserBlockedList } from "../../../../../../../redux/slices/portfolio/actions";
import { BlockedListItem } from "../../../../../../../redux/slices/portfolio/types";
import { useDispatch, useSelector } from "react-redux";
import TableSkeleton from "../../../../../../../component/common/TableSkeleton";
import { usePagination } from "../../../../../../../hooks/usePagination";

interface BlockedListTableProps {
  userId: string;
}

const BlockedUsersTable: React.FC<BlockedListTableProps> = ({userId}) => {

  const dispatch = useDispatch<AppDispatch>();
  const { blockedList, blockedListLoading } = useSelector((state: RootState) => state.portfolio); 
  
  const columns = useMemo<ColumnDef<BlockedListItem>[]>(() => [
    {
      header: "Invited Users",
      accessorKey: "full_name",
      cell: (info) => {
        const row = info.row.original;
        return (
          <UserCard
            userName={row.full_name || 'N/A'}
            userHandle={row.username}
            userImage={row.profile_image}
            />
        );
      },
      sortingFn: (rowA, rowB, columnId) => {
          const nameA = rowA.getValue<string | null>(columnId) || '';
          const nameB = rowB.getValue<string | null>(columnId) || '';

          if (!nameA && nameB) return -1;
          if (nameA && !nameB) return 1;
          if (!nameA && !nameB) return 0;

          return nameA.localeCompare(nameB);
      }
    },
    {
      header: "Email-id",
      accessorKey: "email",
      cell: (info) => info.getValue() || 'N/A',
    },
    {
      header: "Location",
      accessorKey: "location",
      cell: (info) => info.getValue() || 'N/A',
    },
    {
      header: "Status",
      accessorKey: "status",
      cell: (info) => {
        const value = info.getValue<string>();
        return <span className={cn("statusBadge", value?.toLowerCase())}>{value}</span>;
      },
    },
  ], []);
  
  const [sorting, setSorting] = useState<SortingState>([]);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 5 });
  
  useEffect(() => {
     if (userId) {
       dispatch(fetchUserBlockedList({ userId, page: pagination.pageIndex + 1, limit: pagination.pageSize }));
     }
   }, [dispatch, userId, pagination]);

  const data = useMemo(() => blockedList?.blocked_users || [], [blockedList]);
  const pageCount = blockedList ? blockedList.totalPages : 0;
  
  const table = useReactTable({
    data: data,
    columns,
    state: { sorting, pagination },
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualPagination: true,
    pageCount: pageCount,
    sortDescFirst: false,
  });

  const paginationGroup = usePagination({
      currentPageIndex: pagination.pageIndex,
      totalPages: pageCount,
    });
  
    const pageNumbersElements = paginationGroup.map((page, index) => {
      if (typeof page === 'string') {
        return <span key={`ellipsis-${index}`} className={'pageNumberBtn'} style={{cursor: 'default'}}>{page}</span>;
      }
  
      const pageIndex = page;
      return (
        <CustomButton
          key={pageIndex}
          onClick={() => table.setPageIndex(pageIndex)}
          disabled={pagination.pageIndex === pageIndex}
          btnType={
            pagination.pageIndex === pageIndex
              ? ButtonType.ACTIVENOSTYLE
              : ButtonType.NOSTYLE
          }
          btnSize={ButtonSize.NORMAL}
          btnFont={
            pagination.pageIndex === pageIndex
              ? ButtonFont.BOLD
              : ButtonFont.NORMAL
          }
          className={cn(
            'pageNumberBtn',
            pagination.pageIndex === pageIndex && 'activePageNumberBtn'
          )}
        >
          {pageIndex + 1}
        </CustomButton>
      );
    });
  
  return (
    <div className={'tableContainer'}>
      <div className={'tableWrapper'}>
        <table>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    onClick={header.column.getToggleSortingHandler()}
                    style={{
                      cursor: header.column.getCanSort() ? "pointer" : "default"
                    }}
                  >
                    {flexRender(header.column.columnDef.header, header.getContext())}
                    {header.column.getCanSort() && (
                      <span className={cn('tableShorting')}>
                          <Image 
                              src={header.column.getIsSorted() === "asc" ? ASSESTS.ASSENDING : header.column.getIsSorted() === "desc" ? ASSESTS.DECENDING : ASSESTS.SHORT} 
                              alt="Short" 
                          />
                      </span>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {blockedListLoading === "pending" ? (
                <TableSkeleton
                    columns={columns.length}
                    rows={pagination.pageSize}
                />
            ) : table.getRowModel().rows.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} className={'noResultsFound'}>
                    No results found
                  </td>
                </tr>
            ) : (
            table.getRowModel().rows.map((row) => (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Pagination
        enteriesShows={`${blockedList?.total
            ? pagination.pageIndex * pagination.pageSize + 1
            : 0
        }-${Math.min(
            (pagination.pageIndex + 1) * pagination.pageSize,
            blockedList?.total || 0
        )} of ${blockedList?.total || 0} Entries`}

        pageNumbers={[
          pageCount > 1 && (
            <CustomButton
              key="prev"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
              btnType={ButtonType.NOSTYLE}
              className={'pageNumberBtn'}
            >
              Prev
            </CustomButton>
          ),
          ...pageNumbersElements,
          pageCount > 1 && (
            <CustomButton
              key="next"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
              btnType={ButtonType.NOSTYLE}
              className={'pageNumberBtn'}
            >
              Next
            </CustomButton>
          ),
        ].filter(Boolean)}
        
        dropDownOptions={[
          { value: "5", label: "5" },
          { value: "10", label: "10" },
          { value: "20", label: "20" },
          { value: "25", label: "25" },
        ]}
        dropdownOnChange={(value) => { setPagination(old => ({...old, pageSize: Number(value), pageIndex: 0})); }}
        dropDownPlaceholder={pagination.pageSize.toString()}
      />
    </div>
  );
};

export default React.memo(BlockedUsersTable);
